import {
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import useAppContextProvider from "../../AppContext/useAppContextProvider";
import { AddCSTags } from "../../api/api";

type SiteResult = {
  status: string;
  Msg?: string;
  errorCode?: string;
};

type MinerName = {
  SerialNo: string;
  CSTag: string;
};

type AttachTagsPayloadCS = {
  minernames: MinerName[];
};

export const usePostAttachTagsToMinersCS = () => {
  const queryClient = useQueryClient();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const { getAccessTokenSilently, isAuthenticated } =
    useAuth0();
  const [authToken, setAuthToken] = useState<string | null>(
    null
  );

  const mutation = useMutation({
    mutationFn: ({
      minernames,
      token,
    }: AttachTagsPayloadCS & {
      token: string;
    }): Promise<SiteResult> =>
      AddCSTags({ minernames }, token),

    onSuccess: (data) => {
      if (data.status === "error") {
        setToastMessage(
          `Error code: ${data.errorCode}, Error msg: ${data.Msg}` ||
            "Error adding tag"
        );
      } else {
        setToastMessage("Tag added successfully");
      }
      setToastOpen(true);
    },

    onError: (error) => {
      console.error("Error adding Tag:", error);
      setToastMessage("Error adding Tag");
      setToastOpen(true);
    },
  });

  const mutateWithToken = useCallback(
    async (
      { minernames }: AttachTagsPayloadCS,
      options?: {
        onSuccess?: (data: SiteResult) => void;
        onError?: (error: unknown) => void;
        onSettled?: () => void;
      }
    ) => {
      const token = await getAccessTokenSilently();

        mutation.mutate({
          minernames,
          token,
        },
        {
            onSuccess: options?.onSuccess,
            onError: options?.onError,
            onSettled: options?.onSettled,
        }
      );
    },
    [getAccessTokenSilently, mutation]
  );

  return { ...mutation, mutate: mutateWithToken };
};
